// @flow
import DetailModel from "../detail/DetailModel";
import Href from "../href/Href";
import TaskGroupCollection from "../taskgroup/TaskGroupCollection";
import TaskGroupModel from "../taskgroup/TaskGroupModel";

import { TITLE } from "../../constants/LayoutHints";

import IllegalStateException from "../../exceptions/IllegalStateException";

import type { ModularUIModel, AttributeType } from "../types";
import type { ModularUIResponse } from "../../modularui";
import type LinkCollection from "../links/LinkCollection";
import type LinkModel from "../links/LinkModel";
import type ErrorResponse from "../error/ErrorResponse";

/**
 * Model containing the details of one case.
 */
export default class CaseViewModel extends DetailModel {
  _taskGroupCollection: TaskGroupCollection;

  /**
   */
  constructor(caseviewData: ModularUIResponse) {
    super(caseviewData);

    this.createTaskGroupCollection();
  }

  /**
   */
  get type(): string {
    return "CaseView";
  }

  /**
   */
  static get modelName(): string {
    return "CaseViewModel";
  }

  /**
   */
  static isApplicableModel(data: ModularUIResponse): boolean {
    return (
      data.contributions.resourcetype &&
      data.contributions.resourcetype === "CaseView"
    );
  }

  /**
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    return this.links.getLinksByGroup("taskgroup").all;
  }

  /**
   * Getting panel links
   */
  get panelLinks(): LinkCollection {
    return this.links.getLinksByGroup("panel");
  }

  /**
   */
  setChildModels(models: Array<ModularUIModel>, errors: Array<ErrorResponse>) {
    super.setChildModels(models, errors);

    const taskGroupModels = [];
    for (let i = 0; i < models.length; i++) {
      if (models[i] instanceof TaskGroupModel) {
        taskGroupModels.push(models[i]);
      }
    }

    this.taskGroupCollection.add(taskGroupModels);
  }

  /**
   * Getting the case name
   */
  get casename(): ?AttributeType {
    return this.attributeCollection.getAttributeByLayoutHint(TITLE);
  }

  /**
   * Retrieve the case label, this can be configured with a case property with the layout hint 'title'
   */
  get label(): string {
    return this.casename ? this.casename.readonlyvalue : "";
  }

  /**
   * Retrieve the label that was configured on the caseview
   */
  get viewLabel(): string {
    return this.getContribution("label", "");
  }

  /**
   * Check if an introtext exists for this caseview
   */
  hasIntroText(): boolean {
    return this.introtext !== "";
  }

  /**
   * Getting the introduction text configured on the case view
   */
  get introtext(): string {
    if (this.data._content) {
      return this.data._content.text.message;
    }

    if (this.contributions.text) {
      return this.contributions.text.message ?? this.contributions.text;
    }

    if (Array.isArray(this.contributions.texts)) {
      return this.contributions.texts[0].text;
    }

    return "";
  }

  /**
   * Getting the self href
   */
  get selfhref(): Href {
    const selfLink = this.links.getLinkByKey("self");

    if (selfLink === null) {
      throw new IllegalStateException("No self href available");
    }

    return selfLink.href;
  }

  /**
   */
  createTaskGroupCollection() {
    const allTaskgroupData = this.getData("taskgroups", []);
    const allTaskgroupContributions = this.getContribution("taskgroups", []);

    this.taskGroupCollection = allTaskgroupData
      .filter((taskgroup) =>
        allTaskgroupContributions.some(
          (taskgroupContribution) =>
            taskgroupContribution.name === taskgroup.name,
        ),
      )
      .map((taskgroup) => {
        const taskgroupContributions = allTaskgroupContributions.find(
          (taskgroupContribution) =>
            taskgroupContribution.name === taskgroup.name,
        );

        return TaskGroupModel.create(
          taskgroup.name,
          taskgroup,
          taskgroupContributions,
          this.modelOptions,
        );
      });
  }

  /**
   * Setting the taskgroup panel collection
   */
  set taskGroupCollection(taskgroups: Array<TaskGroupModel>) {
    this._taskGroupCollection = new TaskGroupCollection(taskgroups);
  }

  /**
   * Getting the taskgrouppanels on the tab
   */
  get taskGroupCollection(): TaskGroupCollection {
    return this._taskGroupCollection;
  }

  /**
   * Has taskgroups
   */
  hasTaskGroups(): boolean {
    return this.taskGroupCollection && this.taskGroupCollection.hasItems;
  }
}
